import Joi from 'joi';
// Use the slim PostHog bundle + slim React entry consistently across the
// app — see `posthogClient.ts` for the rationale. PostHog's docs warn:
// "It's crucial to import posthog-js from the same module path throughout
// your application to avoid multiple bundles." The slim React variant
// (resolved via the `posthog-js/react/slim` subpath) injects the client
// via the `<PostHogProvider client={...}>` prop instead of importing the
// SDK itself, so this pairing keeps a single SDK copy in the bundle.
import posthog from 'posthog-js/dist/module.slim';
import { PostHogProvider } from 'posthog-js/react/slim';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { TanstackRouterProvider } from './components/_core/providers/TanstackRouterProvider';
import './config/sentry';
import './i18n/config';
import { TitleCacheProvider } from './providers/TitleCacheProvider';
import { initializeThemeEarly } from './stores/themeStore';
import { initializePostHog, startFeatureFlagPolling } from './utils/feature-flags/posthogClient';

// Initialize theme before React renders to prevent flash of wrong theme
initializeThemeEarly();

//@ts-expect-error BigInt.prototype.toJSON is not defined in TypeScript
BigInt.prototype.toJSON = function () {
    const int = Number.parseInt(this.toString(), 10);
    return int ?? this.toString();
};

// Kick off PostHog initialisation BEFORE React render. The synchronous portion
// of `initializePostHog()` (config read + `posthog.init()` with server-injected
// bootstrap data) runs before this expression returns, so `posthog` has its
// bootstrap feature flags loaded by the time the React tree mounts. The async
// tail (`waitForFeatureFlags` for non-bootstrap users, identification, etc.)
// is allowed to complete in the background — we no longer block first paint
// on it. See `client/src/utils/feature-flags/posthogClient.ts`.
//
// Defence-in-depth: `getFlag`/`getFlagSync` also fall back to reading directly
// from `window.__POSTHOG_BOOTSTRAP__` if called before init completes.
void initializePostHog().then((initialized) => {
    if (initialized) {
        startFeatureFlagPolling(30000);
    }
});

/**
 * Marks the app as mounted on React's first commit. The inline bootstrap script in
 * `index.html` watches `window.__CURIUM_APP_MOUNTED__`: if it is never set (e.g. a
 * boot-critical chunk failed to load and this module's graph never executed), the boot
 * watchdog there recovers instead of leaving the user stuck on the splash. See
 * `client/index.html`.
 */
function MountBeacon() {
    useEffect(() => {
        window.__CURIUM_APP_MOUNTED__ = true;
    }, []);
    return null;
}

const container = document.getElementById('root');
const root = createRoot(container!);

window.Joi = Joi;

root.render(
    // The slim React entry's `PostHogProvider` types its `client` against
    // `posthog-js` (default), even though it accepts any PostHog instance
    // at runtime. Cast through `never` to bridge the slim / default
    // PostHog identity mismatch documented in `posthogClient.ts`.
    <PostHogProvider client={posthog as unknown as never}>
        <TitleCacheProvider>
            <MountBeacon />
            <TanstackRouterProvider />
        </TitleCacheProvider>
    </PostHogProvider>,
);
